import { Transform, Type } from 'class-transformer'
import { Entity } from '..'

import { PaymentOrderItem, PaymentRecipient, PaymentSupportType } from '.'
import { Deal } from '../crm'
import { Employee } from '../hr'
import { Person } from '../persons'
import { AcquisitionType, ClosingReason, ExpenseCategory, Process, ProcessExpense, ProcessStatus } from '../settings'
import { fixPrice, parseToNumber } from '@/utils/general'
import dayjs, { Dayjs } from 'dayjs'

export class PaymentOrder extends Entity {
  @Type(() => PaymentOrderItem)
  items: PaymentOrderItem[]

  @Type(() => PaymentRecipient)
  recipients: PaymentRecipient[]

  @Type(() => Deal)
  deal: Deal

  @Type(() => Employee)
  employee: Employee

  @Type(() => Person)
  person: Person

  @Type(() => ProcessStatus)
  status: ProcessStatus

  @Type(() => ClosingReason)
  closingReason: ClosingReason

  @Type(() => Process)
  process: Process;

  @Type(() => PaymentSupportType)
  supportType: PaymentSupportType

  @Type(() => ExpenseCategory)
  expenseCategory: ExpenseCategory

  @Type(() => AcquisitionType)
  acquisitionType: AcquisitionType;

  @Type(() => Dayjs)
  @Transform(({ value }) => value && dayjs(value), { toClassOnly: true })
  supportDate: Dayjs

  controlNumber: number
  supportNumber: number
  idProcessRecord: number
  idExpenseCategory: number
  idProcess: number
  amount: number
  comment: string

  get paymentItems () {
    return this.items
      ?.flatMap(item => item.recipients)
      ?.flatMap(recipient => recipient.payments)
  }

  get paidTotal () {
    const { paidPayments } = this

    return paidPayments.reduce((acc, item) => acc += item.amount, 0)
  }

  get paidPayments () {
    return this.paymentItems
      ?.filter(item => item?.closingReason?.type?.name === 'successful')
  }

  get paymentsLength () {
    const { paymentItems } = this

    return paymentItems?.length || 0
  }

  get formattedSupportDate () {
    const { supportDate } = this
    const timestamp = Date.parse(supportDate.format('YYYY-MM-DD'))

    if (!isNaN(timestamp)) {
      return this.supportDate
        .format('DD/MM/YYYY')
    }
    return ''
  }

  get expenseDescription () {
    return this.items?.[0]?.processExpense?.expense?.description || ''
  }

  get expenseType () {
    return this.items?.[0]?.processExpense?.expense?.type
  }

  get amountFormatted () {
    const { amount } = this

    if (!amount) return ''

    return fixPrice(amount)
  }

  get dealActive () {
    const { deal } = this

    return deal?.[0]
  }

  get processExpense () {
    return this.items?.[0]?.processExpense
  }

  get payments () {
    const { items } = this
    if (!items?.length) return []
    return items?.flatMap(item => item?.paymentRecipientsTitle)
  }

  get allPaymentsHasClosingReason () {
    const { payments } = this
    return payments?.every(payment => payment?.closingReason?.id)
  }

  get totalPayments () {
    const { payments } = this

    return payments?.reduce((acc, payment) => {
      return acc + parseToNumber(payment?.amount)
    }
    , 0)
  }

  // Alertas
  get commentAlert () {
    const { comment } = this

    if (!comment) return

    return { background: 'red', icon: 'mdi-chat', color: undefined, tooltip: comment }
  }

  get underpaidAlert () {
    const { totalPayments, amount } = this

    if (totalPayments < amount) {
      return {
        background: 'yellow', icon: 'mdi-card-off-outline', color: undefined, tooltip: 'Pago incompleto',
      }
    }
  }

  get overpaidAlert () {
    const { totalPayments, amount } = this

    if (totalPayments > amount) {
      return {
        background: 'red', icon: 'mdi-alert-box-outline', color: undefined, tooltip: 'Exceso de abono',
      }
    }
  }

  get delayedPaymentAlert () {
    const { underpaidAlert, createdAt } = this

    if (Boolean(underpaidAlert) && dayjs().diff(createdAt, 'days') >= 7) {
      return {
        background: 'green', icon: 'mdi-clock-alert-outline', color: undefined, tooltip: 'Pago moroso',
      }
    }
  }

  get multipleExpenseTypesAlert () {
    const { paymentItems } = this

    const firstItemExpenseType = paymentItems?.[0]?.paymentRecipient?.paymentOrderItem?.id

    if (paymentItems?.some(payment => payment?.paymentRecipient?.paymentOrderItem?.id !== firstItemExpenseType)) {
      return {
        background: 'secondary', icon: 'mdi-close-octagon', color: undefined, tooltip: 'Múltiples tipos de gasto',
      }
    }
  }

  get alerts () {
    const { commentAlert, underpaidAlert, overpaidAlert, delayedPaymentAlert, multipleExpenseTypesAlert } = this

    return [
      commentAlert,
      underpaidAlert,
      overpaidAlert,
      delayedPaymentAlert,
      multipleExpenseTypesAlert,
    ].filter(Boolean)
  }
}
